/**
 * What-If Override Helpers
 * Pure functions for layering what-if overrides onto base nodes.
 */

/**
 * Get the active override from a path's whatIfOverrides map.
 * Returns the override with the lowest startIndex (there should only be one at a time).
 */
export function getActiveOverride(whatIfOverrides) {
  if (!whatIfOverrides || typeof whatIfOverrides !== 'object') return null;
  const keys = Object.keys(whatIfOverrides);
  if (keys.length === 0) return null;
  // Return the first (and typically only) override
  const key = keys[0];
  return { key, ...whatIfOverrides[key] };
}

/**
 * Resolve the effective node list by overlaying overrides onto base nodes.
 * Nodes before startIndex are preserved from baseNodes.
 * Nodes at startIndex and after are replaced with overriddenNodes.
 */
export function getResolvedNodes(baseNodes, whatIfOverrides) {
  const override = getActiveOverride(whatIfOverrides);
  if (!override || !override.overriddenNodes) return baseNodes || [];

  const startIndex = override.startIndex;
  const preserved = (baseNodes || []).slice(0, startIndex);
  return [...preserved, ...override.overriddenNodes];
}

/**
 * Check if a node at a given index is overridden.
 */
export function isNodeOverridden(index, whatIfOverrides) {
  const override = getActiveOverride(whatIfOverrides);
  if (!override) return false;
  return index >= override.startIndex;
}

/**
 * Check if a node at a given index is the start of an override.
 */
export function isOverrideStartNode(index, whatIfOverrides) {
  const override = getActiveOverride(whatIfOverrides);
  if (!override) return false;
  return index === override.startIndex;
}

/**
 * Get the original node at a given index (before override was applied).
 */
export function getOriginalNodeAt(index, whatIfOverrides) {
  const override = getActiveOverride(whatIfOverrides);
  if (!override || !override.originalNodes) return null;
  if (index < override.startIndex) return null;
  const offsetIndex = index - override.startIndex;
  return override.originalNodes[offsetIndex] || null;
}
